// Service pour l'API Stable Diffusion (alternative à DALL-E)
const STABLE_DIFFUSION_API_KEY = process.env.REACT_APP_STABLE_DIFFUSION_API_KEY;
const STABLE_DIFFUSION_API_URL = 'https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image';

/**
 * Génère une image avec Stable Diffusion
 * @param {string} prompt - Description de l'image à générer
 * @param {number} steps - Nombre d'étapes (10-50, défaut: 30)
 * @param {number} cfg_scale - Échelle CFG (1-20, défaut: 7)
 * @param {number} width - Largeur de l'image (512-1024, défaut: 1024)
 * @param {number} height - Hauteur de l'image (512-1024, défaut: 1024)
 * @returns {Promise<Object>} - URL de l'image générée
 */
export const generateImageWithStableDiffusion = async (
    prompt, 
    steps = 30, 
    cfg_scale = 7, 
    width = 1024, 
    height = 1024,
    stylePreset = "photographic"
) => {
    if (!STABLE_DIFFUSION_API_KEY) {
        throw new Error('Clé API Stable Diffusion manquante. Ajoutez REACT_APP_STABLE_DIFFUSION_API_KEY dans votre .env');
    }

    try {
        console.log('🎨 Génération d\'image Stable Diffusion:', prompt);
        
        const response = await fetch(STABLE_DIFFUSION_API_URL, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${STABLE_DIFFUSION_API_KEY}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                text_prompts: [
                    {
                        text: prompt,
                        weight: 1
                    }
                ],
                cfg_scale: cfg_scale,
                height: height,
                width: width,
                samples: 1,
                steps: steps,
                style_preset: stylePreset // Options: photographic, digital-art, cinematic, anime, manga, fantasy-art, neon-punk, isometric, low-poly, origami, line-art, craft-clay, 3d-model, pixel-art, tile-texture
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`Erreur Stable Diffusion: ${errorData.message || 'Erreur inconnue'}`);
        }

        const data = await response.json();
        console.log('✅ Image Stable Diffusion générée avec succès:', data);
        
        // Stable Diffusion retourne l'image en base64
        const imageData = data.artifacts[0];
        const imageUrl = `data:image/png;base64,${imageData.base64}`;
        
        return {
            url: imageUrl,
            prompt: prompt,
            created: new Date().toISOString()
        };
    } catch (error) {
        console.error('❌ Erreur génération image Stable Diffusion:', error);
        throw error;
    }
};

/**
 * Génère une image avec des paramètres optimisés pour les affiches de films
 * @param {string} prompt - Description de l'affiche
 * @returns {Promise<Object>} - URL de l'image générée
 */
export const generateMoviePoster = async (prompt, options = {}) => {
    const {
        width = 1024,
        height = 1024,
        steps = 40,
        cfgScale = 8,
        stylePreset = "cinematic"
    } = options;
    return generateImageWithStableDiffusion(
        `${prompt}, movie poster, cinematic, dramatic lighting, professional photography`,
        steps,
        cfgScale,
        width,
        height,
        stylePreset
    );
};

/**
 * Vérifie si l'API Stable Diffusion est disponible
 * @returns {boolean}
 */
export const isStableDiffusionAvailable = () => {
    return !!STABLE_DIFFUSION_API_KEY;
};

/**
 * Liste des styles disponibles pour Stable Diffusion
 */
export const STABLE_DIFFUSION_STYLES = {
    PHOTOGRAPHIC: "photographic",
    DIGITAL_ART: "digital-art", 
    CINEMATIC: "cinematic",
    ANIME: "anime",
    MANGA: "manga",
    FANTASY_ART: "fantasy-art",
    NEON_PUNK: "neon-punk",
    ISOMETRIC: "isometric",
    LOW_POLY: "low-poly",
    ORIGAMI: "origami",
    LINE_ART: "line-art",
    CRAFT_CLAY: "craft-clay",
    THREE_D_MODEL: "3d-model",
    PIXEL_ART: "pixel-art",
    TILE_TEXTURE: "tile-texture"
}; 
